/* Jenevah — theme behaviors
   Ported from reference-design.html. Adapted so review filter/sort/helpful/load-more
   operate on server-rendered (Liquid) DOM nodes instead of a hardcoded JS array —
   review content has to stay editable in the theme editor. Star markup, the "N/5"
   summary line and the buyer-photo scroll strip are rendered directly by Liquid
   (no client templating needed there). The reference's before/after block is a
   static image, not a slider, so there's no slider JS to port. The reference's
   contact-form handler was a fake demo submit (`preventDefault` + "not connected
   yet"); this theme's contact form is the real Shopify contact-form, so that fake
   handler is dropped rather than ported.

   Every init below is idempotent and re-runs on 'shopify:section:load': the theme
   editor's live preview injects/replaces section HTML asynchronously, which can
   land *after* this script (loaded with `defer`) has already made its one pass —
   a real storefront visit doesn't have that race, but the editor does. Guarding
   with a `data-jenevah-init` flag keeps a second run on an unchanged element a
   no-op instead of double-binding listeners. */
(function () {
  'use strict';

  function once(el, fn) {
    if (!el || el.dataset.jenevahInit === '1') return;
    el.dataset.jenevahInit = '1';
    fn();
  }

  /* --- mobile menu --- */
  function initMobileMenu() {
    var burger = document.getElementById('burger');
    var mnav = document.getElementById('mnav');
    once(burger, function () {
      burger.addEventListener('click', function () {
        var open = mnav.getAttribute('data-open') === '1';
        mnav.setAttribute('data-open', open ? '0' : '1');
        burger.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });
  }

  /* --- 100-slot grid --- */
  function initHundredGrids() {
    document.querySelectorAll('[data-hundred]').forEach(function (el) {
      once(el, function () {
        var filled = parseInt(el.getAttribute('data-hundred'), 10) || 0;
        var frag = document.createDocumentFragment();
        for (var i = 0; i < 100; i++) {
          var s = document.createElement('i');
          if (i < filled) s.className = 'on';
          frag.appendChild(s);
        }
        el.appendChild(frag);
      });
    });
  }

  /* --- product gallery: thumbnail click swaps the main image (or, for a
     buyer-submitted video thumb, plays the video in place of the image) --- */
  function initProductGallery() {
    var main = document.getElementById('galMain');
    var vid = document.getElementById('galMainVid');
    var thumbs = document.getElementById('galThumbs');
    if (!main) return;
    once(thumbs, function () {
      thumbs.addEventListener('click', function (e) {
        var b = e.target.closest('button');
        if (!b) return;
        thumbs.querySelectorAll('button').forEach(function (x) {
          x.setAttribute('aria-current', x === b ? 'true' : 'false');
        });

        var videoSrc = b.getAttribute('data-video');
        if (videoSrc && vid) {
          main.style.display = 'none';
          vid.src = videoSrc;
          vid.poster = b.getAttribute('data-src') || '';
          vid.style.display = 'block';
          var p = vid.play();
          if (p && p.catch) p.catch(function () {});
          return;
        }

        if (vid) { vid.pause(); vid.style.display = 'none'; }
        main.style.display = 'block';
        main.src = b.getAttribute('data-src');
        var img = b.querySelector('img');
        main.alt = img ? img.alt : main.alt;
      });
    });
  }

  /* --- bundle selection ---
     Each .opt wraps a real variant-picker radio input (name="id", value=variant GID)
     plus data-price / data-label on the <label> for the button text. Selecting an
     .opt drives the real Shopify variant input, so add-to-cart submits the right
     variant — this is the "restyled native plumbing" the pack cards sit on top of. */
  function initBundleSelection() {
    var opts = Array.prototype.slice.call(document.querySelectorAll('.opt'));
    if (!opts.length) return;
    var buyTargets = ['buy', 'buy2', 'stickyBtn'].map(function (id) {
      return document.getElementById(id);
    }).filter(Boolean);

    function syncBuyLabels(opt) {
      var price = opt.getAttribute('data-price');
      if (!price) return;
      buyTargets.forEach(function (btn) {
        var prefix = btn.getAttribute('data-label-prefix') || 'Shop now';
        btn.textContent = prefix + ' — ' + price;
      });
    }

    opts.forEach(function (o) {
      var input = o.querySelector('input[type="radio"]');
      if (!input) return;
      once(o, function () {
        input.addEventListener('change', function () {
          opts.forEach(function (x) { x.removeAttribute('data-on'); });
          o.setAttribute('data-on', '1');
          syncBuyLabels(o);
        });
      });
      if (input.checked) syncBuyLabels(o);
    });
  }

  /* --- sticky buy bar --- */
  function initStickyBar() {
    var sticky = document.getElementById('sticky');
    if (!sticky || !('IntersectionObserver' in window)) return;
    once(sticky, function () {
      var anchor = document.querySelector('.hero') || document.querySelector('.phead') || document.querySelector('.pdp');
      if (anchor) {
        new IntersectionObserver(function (entries) {
          sticky.setAttribute('data-show', entries[0].isIntersecting ? '0' : '1');
        }, { threshold: 0 }).observe(anchor);
      } else {
        sticky.setAttribute('data-show', '1');
      }
    });
  }

  /* =====================================================================
     REVIEWS: filter / sort / helpful-vote / load-more
     Operates on .rev cards already rendered server-side inside #revGrid.
     Each card carries data-stars, data-has-media ("1"/"0") and data-days
     (a relative "days since" number rendered by Liquid at page-render time)
     so this can filter/sort without re-fetching or re-templating anything.
     ===================================================================== */
  function initReviewsController() {
    var grid = document.getElementById('revGrid');
    if (!grid) return;

    /* Cards can arrive after this controller's first pass — the editor's live
       preview sometimes injects a section's blocks asynchronously, landing
       after `initAll()` already ran once with an empty grid. So the card list,
       state and render() are rebuilt fresh on every call (cheap, and picks up
       newly-arrived cards); only the event *listeners* on the persistent
       controls are bound once, via `once()`, to avoid stacking duplicates. */
    {
      var PAGE_SIZE = 6;
      var cards = Array.prototype.slice.call(grid.querySelectorAll('.rev'));
      var shown = PAGE_SIZE;
      var state = { filter: 'all', sort: 'recent' };

      function passes(card) {
        if (state.filter === 'all') return true;
        if (state.filter === 'photos') return card.getAttribute('data-has-media') === '1';
        var stars = Number(card.getAttribute('data-stars'));
        if (state.filter === 'low') return stars <= 3;
        return stars === Number(state.filter);
      }

      function sortCompare(a, b) {
        var sa = Number(a.getAttribute('data-stars'));
        var sb = Number(b.getAttribute('data-stars'));
        var da = Number(a.getAttribute('data-days'));
        var db = Number(b.getAttribute('data-days'));
        var ha = Number(a.getAttribute('data-helpful')) || 0;
        var hb = Number(b.getAttribute('data-helpful')) || 0;
        if (state.sort === 'highest') return (sb - sa) || (da - db);
        if (state.sort === 'lowest') return (sa - sb) || (da - db);
        if (state.sort === 'helpful') return hb - ha;
        return da - db; /* recent: fewer days-ago first */
      }

      var countEl = document.getElementById('revCount');
      var moreWrap = document.getElementById('revMore');

      function render() {
        var matching = cards.filter(passes).sort(sortCompare);

        /* .rev sets its own `display: flex` (from the reference CSS), which
           outranks the UA stylesheet's [hidden] rule at equal specificity —
           so hiding has to be an explicit inline style, not the hidden
           attribute, or "hidden" cards stay visible. */
        cards.forEach(function (c) { c.style.display = 'none'; });
        matching.slice(0, shown).forEach(function (c) {
          c.style.display = '';
          grid.appendChild(c); /* reorders in place */
        });

        if (countEl) {
          countEl.textContent = matching.length
            ? 'Showing ' + Math.min(shown, matching.length) + ' of ' + matching.length + ' reviews'
            : '';
        }

        if (moreWrap) {
          var remaining = matching.length - shown;
          moreWrap.innerHTML = remaining > 0
            ? '<button class="btn btn--ghost" type="button" id="revMoreBtn">Load ' + Math.min(PAGE_SIZE, remaining) + ' more reviews</button>'
            : '';
          var mb = document.getElementById('revMoreBtn');
          if (mb) mb.addEventListener('click', function () { shown += PAGE_SIZE; render(); });
        }

        var empty = grid.querySelector('.rempty[data-generated]');
        if (!matching.length) {
          if (!empty) {
            empty = document.createElement('div');
            empty.className = 'rempty';
            empty.setAttribute('data-generated', '1');
            empty.style.gridColumn = '1/-1';
            empty.textContent = 'No reviews match this filter yet.';
            grid.appendChild(empty);
          }
          empty.style.display = '';
        } else if (empty) {
          empty.style.display = 'none';
        }
      }

      document.querySelectorAll('.chip[data-f]').forEach(function (chip) {
        chip.addEventListener('click', function () {
          document.querySelectorAll('.chip[data-f]').forEach(function (x) {
            x.setAttribute('aria-pressed', 'false');
          });
          chip.setAttribute('aria-pressed', 'true');
          state.filter = chip.getAttribute('data-f');
          shown = PAGE_SIZE;
          render();
        });
      });

      var sortSelect = document.getElementById('revSort');
      if (sortSelect) {
        sortSelect.addEventListener('change', function () {
          state.sort = sortSelect.value;
          shown = PAGE_SIZE;
          render();
        });
      }

      grid.addEventListener('click', function (e) {
        var b = e.target.closest('.helpful');
        if (!b) return;
        var current = Number(b.getAttribute('data-helpful-count')) || 0;
        var voted = b.getAttribute('data-on') === '1';
        var next = voted ? current - 1 : current + 1;
        b.setAttribute('data-on', voted ? '0' : '1');
        b.setAttribute('aria-pressed', voted ? 'false' : 'true');
        b.setAttribute('data-helpful-count', String(next));
        var span = b.querySelector('span');
        if (span) span.textContent = '(' + next + ')';
        b.closest('.rev').setAttribute('data-helpful', String(next));
      });

      render();
    }
  }

  function initAll() {
    initMobileMenu();
    initHundredGrids();
    initProductGallery();
    initBundleSelection();
    initStickyBar();
    initReviewsController();
  }

  initAll();

  /* Theme editor: re-run after a section's HTML is (re)injected. Harmless
     no-op on a real storefront visit, where this event never fires. */
  document.addEventListener('shopify:section:load', initAll);
})();
